import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarPane } from '../components/CalendarPane';
import { CommandPalette, type Command } from '../components/CommandPalette';
import { ConflictLog } from '../components/ConflictLog';
import { FilterBar } from '../components/FilterBar';
import { ItemEditor } from '../components/ItemEditor';
import { ListDialog } from '../components/ListDialog';
import { ListPane } from '../components/ListPane';
import { QuickAdd } from '../components/QuickAdd';
import { Settings } from '../components/Settings';
import { Sidebar } from '../components/Sidebar';
import { TagDialog } from '../components/TagDialog';
import {
  createGlobalTag,
  createItem,
  createList,
  createListTag,
  db,
  deleteGlobalTag,
  deleteList,
  deleteListTag,
  findOrCreateGlobalTag,
  newItem,
  reorder,
  restore,
  softDelete,
  toggleDone,
  updateGlobalTag,
  updateItem,
  updateList,
  updateListTag,
} from '../db/dexie';
import {
  INBOX_LIST_ID,
  TRASH_RETENTION_DAYS,
  emptyView,
  type GlobalTag,
  type Item,
  type List,
  type ListTag,
  type Selection,
  type View,
} from '../db/types';
import {
  useAttachmentCounts,
  useCatalog,
  useConflicts,
  useItems,
  useSearchIndex,
} from '../hooks/useData';
import { syncLabel, type SyncStatus } from '../hooks/useSync';
import { useUndo } from '../hooks/useUndo';
import { exportJSON } from '../lib/export';
import { shiftMonthKey, thisMonthKey } from '../lib/dates';
import { inferKind } from '../lib/quickadd';
import { calendarDays, itemsFor, sectionsFor, viewCounts, viewMeta } from '../lib/views';
import { describeError } from '../sync/engine';
import { purgeItem } from '../sync/purge';
import { isConfigured, supabase } from '../sync/supabase';
import { Dialog } from '../components/Dialog';
import './DesktopShell.css';

const EMPTY_STATES: Record<string, { title: string; hint: string }> = {
  today: { title: 'Nothing due today', hint: 'Anything with a future start date is waiting in Upcoming.' },
  upcoming: { title: 'Nothing scheduled', hint: 'The next fourteen days are clear.' },
  inbox: { title: 'Inbox is empty', hint: 'Captures with no list chosen land here.' },
  all: { title: 'Nothing yet', hint: 'Add the first task or note above.' },
  trash: {
    title: 'Trash is empty',
    hint: `Deleted items stay here for ${TRASH_RETENTION_DAYS} days. Deleting again from here removes one for good.`,
  },
  list: { title: 'This list is empty', hint: 'Add something above, or move a note in from elsewhere.' },
  listTag: { title: 'Nothing with this tag', hint: 'List tags only ever match inside their own list.' },
  globalTag: { title: 'Nothing with this tag', hint: 'Global tags match across every list.' },
};

type TagDialogState =
  | { scope: 'list'; listId: string; id?: string; name?: string; color?: string | null }
  | { scope: 'global'; id?: string; name?: string; color?: string | null };

/** Three panes: sidebar, list, editor. Everything reachable from the keyboard. */
export function DesktopShell({ sync }: { sync: SyncStatus }) {
  const items = useItems();
  const catalog = useCatalog();
  const { lists, listTags, globalTags } = catalog;
  const conflicts = useConflicts();
  const imageCounts = useAttachmentCounts();
  const index = useSearchIndex(items);
  const { offer } = useUndo();

  const [view, setView] = useState<View>(() => emptyView({ type: 'today' }));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [expandedListId, setExpandedListId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Item | null>(null);
  const [listDialog, setListDialog] = useState<{ list?: List } | null>(null);
  const [tagDialog, setTagDialog] = useState<TagDialogState | null>(null);
  const [purging, setPurging] = useState<Item | null>(null);

  const sections = useMemo(() => sectionsFor(view, items), [view, items]);
  const counts = useMemo(() => viewCounts(items, catalog), [items, catalog]);
  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const selected = useMemo(
    () => items.find((i) => i.id === selectedId && !i.deleted) ?? null,
    [items, selectedId],
  );
  const meta = viewMeta(view, catalog, flat.length);
  const dateGrouped = view.selection.type === 'today' || view.selection.type === 'upcoming';

  const month = view.selection.type === 'calendar' ? view.selection.month : null;
  const days = useMemo(
    () => (month === null ? [] : calendarDays(month, itemsFor(view, items))),
    [month, view, items],
  );

  /* ── Navigation ───────────────────────────────────────── */

  const select = useCallback((selection: Selection) => {
    // The stacked filters belong to the place you were, not the place you are
    // going — carrying them across would silently hide things.
    setView(emptyView(selection));
    setSelectedId(null);
    setDraft(null);
  }, []);

  const stackGlobalTag = useCallback((tagId: string) => {
    setView((current) => ({
      ...current,
      globalTagIds: current.globalTagIds.includes(tagId)
        ? current.globalTagIds.filter((id) => id !== tagId)
        : [...current.globalTagIds, tagId],
    }));
  }, []);

  const openItem = useCallback((item: Item) => {
    setDraft(null);
    setSelectedId(item.id);
  }, []);

  /* ── Calendar ─────────────────────────────────────────── */

  const goToMonth = useCallback((next: string) => {
    setView((current) =>
      current.selection.type === 'calendar'
        ? { ...current, selection: { ...current.selection, month: next } }
        : current,
    );
  }, []);

  const shiftMonth = useCallback(
    (by: number) => {
      if (month !== null) goToMonth(shiftMonthKey(month, by));
    },
    [goToMonth, month],
  );

  /**
   * Dropping an item on a day moves its deadline there and keeps whatever
   * clock time it already had — a 9am standup dragged to Thursday is still at
   * 9am, and losing that on every drag would make the gesture untrustworthy.
   *
   * A note with no date lands on `created_at`; setting `due_at` here is what
   * pins it, which is the same field the editor's date picker writes.
   */
  const reschedule = useCallback((item: Item, day: Date) => {
    const previous = item.due_at ?? item.start_at;
    const next = new Date(day);
    if (previous) {
      const old = new Date(previous);
      next.setHours(old.getHours(), old.getMinutes(), 0, 0);
    } else {
      next.setHours(0, 0, 0, 0);
    }
    void updateItem(item.id, { due_at: next.toISOString() });
  }, []);

  /* ── Composer ─────────────────────────────────────────── */

  const composeDefaults = useCallback((): Partial<Item> => {
    const s = view.selection;
    return {
      list_id: s.type === 'list' || s.type === 'listTag' ? s.listId : INBOX_LIST_ID,
      list_tag_ids: s.type === 'listTag' ? [s.tagId] : [],
      global_tag_ids: s.type === 'globalTag' ? [s.tagId] : view.globalTagIds,
    };
  }, [view]);

  const compose = useCallback(() => {
    setSelectedId(null);
    setDraft((current) => current ?? newItem({ kind: 'note', ...composeDefaults() }));
  }, [composeDefaults]);

  /** Starting a note from a day in the grid dates it to that day. */
  const composeOn = useCallback(
    (day: Date) => {
      const at = new Date(day);
      at.setHours(0, 0, 0, 0);
      setSelectedId(null);
      setDraft(newItem({ kind: 'note', ...composeDefaults(), due_at: at.toISOString() }));
    },
    [composeDefaults],
  );

  const promoting = useRef(false);
  /** Keystrokes that arrived while the first write was still in flight. */
  const missed = useRef<Partial<Item> | null>(null);
  const patchDraft = useCallback(
    async (patch: Partial<Item>) => {
      // The row is being written for the first time. Dropping what is typed
      // during that write would lose text the user can see on screen, so it is
      // held back and applied the moment the row exists.
      if (promoting.current) {
        missed.current = { ...missed.current, ...patch };
        return;
      }
      if (!draft) return;
      const next = { ...draft, ...patch, updated_at: new Date().toISOString() };

      if (!next.title.trim() && !next.body.trim()) {
        setDraft(next);
        return;
      }
      promoting.current = true;
      try {
        await db.items.put(next);
        const late = missed.current;
        missed.current = null;
        if (late) await updateItem(next.id, late);
      } finally {
        promoting.current = false;
      }
      setDraft(null);
      setSelectedId(next.id);
    },
    [draft],
  );

  /* ── Item actions ─────────────────────────────────────── */

  const remove = useCallback(
    (item: Item) => {
      // Already in the Trash: the second press is the one that means it. There
      // is no undo for a purge, so this is one of the two things in the app
      // that stops to ask.
      if (item.deleted) {
        setPurging(item);
        return;
      }
      void softDelete(item.id);
      if (item.id === selectedId) setSelectedId(null);
      offer('Moved to Trash', () => void restore(item.id));
    },
    [offer, selectedId],
  );

  const confirmPurge = useCallback(async () => {
    if (!purging) return;
    const item = purging;
    setPurging(null);
    try {
      await purgeItem(item.id);
      if (item.id === selectedId) setSelectedId(null);
    } catch (cause) {
      // Most likely the server could not be reached. Purging locally anyway
      // would leave a live row behind for the next pull to hand straight back.
      offer(`Could not delete · ${describeError(cause)}`, () => {});
    }
  }, [offer, purging, selectedId]);

  const complete = useCallback(
    (item: Item) => {
      void toggleDone(item);
      if (!item.done) offer('Task completed', () => void toggleDone({ ...item, done: true }));
    },
    [offer],
  );

  /* ── Organisation actions ─────────────────────────────── */

  const removeList = useCallback(
    async (list: List) => {
      const moved = await deleteList(list.id);
      if (expandedListId === list.id) setExpandedListId(null);
      if (view.selection.type !== 'today') select({ type: 'today' });
      offer(
        moved > 0 ? `List deleted · ${moved} moved to Inbox` : 'List deleted',
        () => void updateList(list.id, { deleted: false }),
      );
    },
    [expandedListId, offer, select, view.selection.type],
  );

  const removeListTag = useCallback(
    async (tag: ListTag) => {
      await deleteListTag(tag.id);
      offer('Tag deleted', () => void updateListTag(tag.id, { deleted: false }));
    },
    [offer],
  );

  const removeGlobalTag = useCallback(
    async (tag: GlobalTag) => {
      await deleteGlobalTag(tag.id);
      offer('Tag deleted', () => void updateGlobalTag(tag.id, { deleted: false }));
    },
    [offer],
  );

  /* ── Commands ─────────────────────────────────────────── */

  const commands = useMemo<Command[]>(
    () => [
      { id: 'today', label: 'Go to Today', shortcut: '⌘1', run: () => select({ type: 'today' }) },
      { id: 'upcoming', label: 'Go to Upcoming', shortcut: '⌘2', run: () => select({ type: 'upcoming' }) },
      {
        id: 'calendar',
        label: 'Go to Calendar',
        shortcut: '⌘3',
        run: () => select({ type: 'calendar', month: thisMonthKey() }),
      },
      { id: 'inbox', label: 'Go to Inbox', shortcut: '⌘4', run: () => select({ type: 'inbox' }) },
      { id: 'all', label: 'Go to All Notes', shortcut: '⌘5', run: () => select({ type: 'all' }) },
      { id: 'new-note', label: 'New note', shortcut: '⌘⇧N', run: compose },
      { id: 'new-list', label: 'New list', run: () => setListDialog({}) },
      { id: 'new-global-tag', label: 'New global tag', run: () => setTagDialog({ scope: 'global' }) },
      { id: 'conflicts', label: 'Open conflict log', shortcut: '⌘⇧L', run: () => select({ type: 'conflicts' }) },
      { id: 'trash', label: 'Open Trash', run: () => select({ type: 'trash' }) },
      { id: 'settings', label: 'Open Settings', run: () => select({ type: 'settings' }) },
      { id: 'export', label: 'Export JSON', shortcut: '⌘⇧E', run: () => void exportJSON() },
      { id: 'sync', label: 'Sync now', run: sync.syncNow },
      ...(isConfigured
        ? [{ id: 'signout', label: 'Sign out of this device', run: () => void supabase?.auth.signOut() }]
        : []),
    ],
    [select, compose, sync.syncNow],
  );

  /* ── Keyboard ─────────────────────────────────────────── */

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      const target = e.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable === true;

      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (mod && e.shiftKey && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        compose();
        return;
      }
      if (mod && e.shiftKey && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        void exportJSON();
        return;
      }
      if (mod && e.shiftKey && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        select({ type: 'conflicts' });
        return;
      }
      // Numbered in sidebar order, so the digit is the row's position.
      if (mod && ['1', '2', '3', '4', '5'].includes(e.key)) {
        e.preventDefault();
        const type = (['today', 'upcoming', 'calendar', 'inbox', 'all'] as const)[
          Number(e.key) - 1
        ]!;
        select(type === 'calendar' ? { type, month: thisMonthKey() } : { type });
        return;
      }

      if (typing || mod) return;

      // Month paging, only while the grid is on screen. `[` and `]` stay clear
      // of the j/k row-stepping the list pane owns.
      if (month !== null) {
        if (e.key === '[') {
          e.preventDefault();
          shiftMonth(-1);
          return;
        }
        if (e.key === ']') {
          e.preventDefault();
          shiftMonth(1);
          return;
        }
        if (e.key === 't') {
          e.preventDefault();
          goToMonth(thisMonthKey());
          return;
        }
      }

      if (e.key === 'j' || e.key === 'k' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (flat.length === 0) return;
        e.preventDefault();
        const step = e.key === 'j' || e.key === 'ArrowDown' ? 1 : -1;
        const at = flat.findIndex((i) => i.id === selectedId);
        const next = at === -1 ? (step === 1 ? 0 : flat.length - 1) : at + step;
        setSelectedId(flat[Math.max(0, Math.min(flat.length - 1, next))]!.id);
      } else if (e.key === ' ' && selected?.kind === 'todo') {
        e.preventDefault();
        complete(selected);
      } else if ((e.key === 'Backspace' || e.key === 'Delete') && selected) {
        e.preventDefault();
        remove(selected);
      }
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flat, selected, selectedId, complete, remove, select, compose, month, shiftMonth, goToMonth]);

  const empty = EMPTY_STATES[view.selection.type] ?? { title: 'Nothing here', hint: '' };

  return (
    <div className={`shell shell-desktop${month !== null ? ' shell--calendar' : ''}`}>
      <Sidebar
        view={view}
        lists={lists}
        listTags={listTags}
        globalTags={globalTags}
        counts={counts}
        status={syncLabel(sync)}
        statusDetail={sync.error}
        expandedListId={expandedListId}
        onExpandList={setExpandedListId}
        onSelect={select}
        onStackGlobalTag={stackGlobalTag}
        onSync={sync.syncNow}
        onNewList={() => setListDialog({})}
        onEditList={(list) => setListDialog({ list })}
        onDeleteList={(list) => void removeList(list)}
        onReorderList={(id, i) => void reorder(db.lists, lists, id, i)}
        onNewListTag={(listId) => setTagDialog({ scope: 'list', listId })}
        onEditListTag={(tag) => setTagDialog({ scope: 'list', listId: tag.list_id, ...tag })}
        onDeleteListTag={(tag) => void removeListTag(tag)}
        onNewGlobalTag={() => setTagDialog({ scope: 'global' })}
        onEditGlobalTag={(tag) => setTagDialog({ scope: 'global', ...tag })}
        onDeleteGlobalTag={(tag) => void removeGlobalTag(tag)}
        onReorderGlobalTag={(id, i) => void reorder(db.globalTags, globalTags, id, i)}
      />

      {view.selection.type === 'conflicts' ? (
        <div className="pane">
          <header className="pane__head">
            <div className="pane__heading">
              <h1 className="pane__title">Conflict log</h1>
              <span className="pane__detail">local only</span>
            </div>
          </header>
          <ConflictLog conflicts={conflicts} />
        </div>
      ) : view.selection.type === 'settings' ? (
        <Settings catalog={catalog} onNewList={() => setListDialog({})} />
      ) : month !== null ? (
        <CalendarPane
          month={month}
          days={days}
          catalog={catalog}
          selectedId={selectedId}
          onShiftMonth={shiftMonth}
          onGoToMonth={goToMonth}
          onOpen={openItem}
          onCompose={composeOn}
          onReschedule={reschedule}
        />
      ) : (
        <ListPane
          title={meta.title}
          detail={meta.detail}
          sections={sections}
          catalog={catalog}
          imageCounts={imageCounts}
          selectedId={selectedId}
          dateStyle={dateGrouped ? 'time' : 'full'}
          emptyTitle={empty.title}
          emptyHint={empty.hint}
          filters={
            view.globalTagIds.length > 0 ? (
              <FilterBar
                tags={globalTags.filter((t) => view.globalTagIds.includes(t.id))}
                onRemove={stackGlobalTag}
                onClear={() => setView((v) => ({ ...v, globalTagIds: [] }))}
              />
            ) : undefined
          }
          quickAdd={
            view.selection.type === 'trash' ? undefined : (
              <QuickAdd
                lists={lists}
                onEnter={compose}
                onSubmit={async (parsed, raw) => {
                  const defaults = composeDefaults();
                  const globalIds = [...(defaults.global_tag_ids ?? [])];
                  // `@name` in quick add is a global tag: it is the only kind
                  // that means anything before you know which list it lands in.
                  for (const name of parsed.tags) {
                    const tag = await findOrCreateGlobalTag(name);
                    if (!globalIds.includes(tag.id)) globalIds.push(tag.id);
                  }
                  const item = await createItem({
                    ...defaults,
                    kind: inferKind(raw),
                    title: parsed.title || raw,
                    due_at: parsed.due_at ?? defaultDue(view),
                    priority: parsed.priority,
                    list_id: parsed.list_id ?? defaults.list_id ?? INBOX_LIST_ID,
                    global_tag_ids: globalIds,
                  });
                  setSelectedId(item.id);
                }}
              />
            )
          }
          onOpen={openItem}
          onToggle={complete}
          onDelete={remove}
        />
      )}

      {draft ? (
        <ItemEditor
          item={draft}
          lists={lists}
          allItems={items}
          crumb="New note"
          draft
          onPatch={patchDraft}
          onDelete={() => setDraft(null)}
          onOpenItem={openItem}
        />
      ) : selected ? (
        <ItemEditor
          item={selected}
          lists={lists}
          allItems={items}
          crumb={`${meta.title} / ${selected.title || 'Untitled'}`}
          onPatch={(patch) => void updateItem(selected.id, patch)}
          onDelete={() => remove(selected)}
          onOpenItem={openItem}
        />
      ) : (
        <div className="shell__blank">
          <div className="empty">
            <div className="empty__title">Nothing selected</div>
            <div className="empty__hint">
              Pick an item, press <span className="mono">⌘⇧N</span> for a new note, or{' '}
              <span className="mono">⌘K</span> to search everything.
            </div>
          </div>
        </div>
      )}

      {listDialog && (
        <ListDialog
          initial={listDialog.list}
          editing={Boolean(listDialog.list)}
          onClose={() => setListDialog(null)}
          onSave={async ({ name, color }) => {
            if (listDialog.list) await updateList(listDialog.list.id, { name, color });
            else {
              const list = await createList({ name, color });
              setExpandedListId(list.id);
              select({ type: 'list', listId: list.id });
            }
            setListDialog(null);
          }}
        />
      )}

      {tagDialog && (
        <TagDialog
          scope={tagDialog.scope}
          listName={
            tagDialog.scope === 'list'
              ? lists.find((l) => l.id === tagDialog.listId)?.name
              : undefined
          }
          initial={tagDialog}
          editing={tagDialog.id !== undefined}
          onClose={() => setTagDialog(null)}
          onSave={async (drafted) => {
            if (tagDialog.id) {
              if (tagDialog.scope === 'list') await updateListTag(tagDialog.id, drafted);
              else await updateGlobalTag(tagDialog.id, drafted);
            } else if (tagDialog.scope === 'list') {
              await createListTag({ ...drafted, list_id: tagDialog.listId });
            } else {
              await createGlobalTag(drafted);
            }
            setTagDialog(null);
          }}
        />
      )}

      {purging && (
        <Dialog
          title="Delete permanently?"
          hint="This cannot be undone, on this device or any other."
          submitLabel="Delete forever"
          onSubmit={() => void confirmPurge()}
          onClose={() => setPurging(null)}
        >
          <p className="dialog__prose">
            <strong>{purging.title || 'Untitled'}</strong> and any images attached to it will be
            destroyed everywhere, instead of waiting out the {TRASH_RETENTION_DAYS}-day retention.
          </p>
        </Dialog>
      )}

      {paletteOpen && (
        <CommandPalette
          items={items.filter((i) => !i.deleted)}
          index={index}
          catalog={catalog}
          commands={commands}
          onOpenItem={openItem}
          onClose={() => setPaletteOpen(false)}
        />
      )}
    </div>
  );
}

/* A capture inherits the context it was made in — adding a task while looking
   at Today means it is due today, not undated. */
const defaultDue = (view: View) =>
  view.selection.type === 'today'
    ? new Date(new Date().setHours(0, 0, 0, 0)).toISOString()
    : null;
